import { useEffect, useState } from 'react';

interface BigResultProps {
  label: string;
  value: number;
  format?: 'currency' | 'number' | 'multiplier' | 'days';
  accent?: 'teal' | 'orange' | 'navy' | 'red' | 'green';
  size?: 'hero' | 'md';
  sublabel?: string;
}

const ACCENT_MAP: Record<string, string> = {
  teal: 'from-navy-800 to-navy-900',
  orange: 'from-orange-500 to-orange-400',
  navy: 'from-navy-900 to-navy-800',
  red: 'from-orange-600 to-orange-400',
  green: 'from-navy-800 to-navy-900',
};

function formatValue(value: number, format: BigResultProps['format']) {
  if (!Number.isFinite(value)) return '—';
  switch (format) {
    case 'currency':
      return value.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
      });
    case 'multiplier':
      return `${value.toFixed(1)}x`;
    case 'days':
      return `${Math.round(value)} days`;
    case 'number':
    default:
      return Math.round(value).toLocaleString('en-US');
  }
}

export default function BigResult({
  label,
  value,
  format = 'currency',
  accent = 'teal',
  size = 'hero',
  sublabel,
}: BigResultProps) {
  const [display, setDisplay] = useState(value);

  useEffect(() => {
    const start = display;
    const end = value;
    if (!Number.isFinite(end) || start === end) {
      setDisplay(end);
      return;
    }
    const duration = 500;
    const startTime = performance.now();
    let raf = 0;

    const tick = (now: number) => {
      const elapsed = now - startTime;
      const t = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      const current = start + (end - start) * eased;
      setDisplay(current);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const gradient = ACCENT_MAP[accent];
  const sizeClass =
    size === 'hero' ? 'text-5xl md:text-6xl lg:text-7xl' : 'text-3xl md:text-4xl';

  return (
    <div className={`bg-gradient-to-br ${gradient} rounded-2xl p-8 text-center shadow-xl`}>
      <p className="text-white/90 text-sm md:text-base font-semibold uppercase tracking-wide mb-3">
        {label}
      </p>
      <p className={`text-orange-400 ${sizeClass} font-display font-bold leading-none`}>
        {formatValue(display, format)}
      </p>
      {sublabel && <p className="text-white/80 text-sm mt-3">{sublabel}</p>}
    </div>
  );
}
